/**
 * v2 workstation tools. One table drives the five new ribbon tabs, the
 * left-hand tool browser and the Run button.
 *
 * `ready` tools call an OpenADS pipeline stage or a desktop inspect command
 * that already exists. `future` tools are shown so the information architecture
 * is complete, but they stay disabled until a wrapper lands.
 */

import type { RibbonItem, RibbonTab } from "@/ribbon/types";
import type { Modality, StageId } from "@/ipc/contract";

export type ToolTabId =
  | "preprocess"
  | "registration"
  | "segmentation"
  | "perfusion"
  | "quantification";

export type ToolStatus = "ready" | "future";

export interface ToolStage {
  modality: Modality;
  id: StageId;
}

export type ToolInspect = "header" | "results" | "viewer" | "viewer-overlay" | "stats";

export interface ToolField {
  id: string;
  label: string;
  kind: "number" | "select";
  value: string;
  options?: { value: string; label: string }[];
}

/**
 * Plug-and-play third-party model with an already-published ONNX export we
 * don't convert ourselves. `"sam-points"` prompts with clicked points;
 * `"sam-box"` would prompt with a dragged box (no model wired to it yet — see
 * `ml.medsam` below). Routed by `ToolLaunchDialog`'s Continue button and
 * `navStore`'s menu-click handler, not by the generic `op`/`stage` run path:
 * neither fits an interactive, multi-step (download, encode, click, decode)
 * session.
 */
export type ToolInteractive = "sam-points" | "sam-box";

/**
 * A real, officially-published, pre-built `@itk-wasm/*` pipeline (not
 * SimpleITK-via-Docker like `op`, not an OpenADS stage like `stage`) run
 * directly in the browser via `tools/itk/*Client.ts`. Dispatched by
 * `StageRunPanel`, same as `isBrainExtraction` dispatches the one real
 * OpenADS stage. `"dicomImport"` is the one kind that does not need an
 * existing case volume first — see `ToolLaunchDialog`'s `skipVolumePick`.
 */
export type ToolItkKind = "downsample" | "compareImages" | "morphInterp" | "dicomImport";

/**
 * A real OpenADS DWI pipeline stage, run standalone outside a full case —
 * same real computation as the main DWI pipeline uses (`lib/dwi-pipeline.ts`,
 * shared with `wasm-engine/engine.ts`), just invoked on whichever volume(s)
 * the user picks rather than as one step of a `sub-{id}` run. `"register"`
 * takes a raw DWI + raw ADC pair; `"segmentStroke"` takes an already
 * MNI-registered DWI + ADC pair (its own output, or the main pipeline's).
 */
export type ToolDwiKind = "register" | "segmentStroke";

export interface ToolDef {
  id: string;
  /**
   * Which ribbon tab and generated Tools-menu bucket this belongs to.
   * Omitted for a tool that only lives in a hand-authored menu group (see
   * `ml.sam`/`ml.medsam` in `nav.config.ts`'s `tools.learning` group) — such a
   * tool is reachable by id via `toolById`, but `toolsForTab`/`toolGroups`/
   * `toolRibbonTabs` correctly never surface it in the ribbon or the
   * auto-generated per-tab menu bucket.
   */
  tab?: ToolTabId;
  group: string;
  label: string;
  icon: string;
  size: "large" | "small";
  description: string;
  engine: string;
  status: ToolStatus;
  stage?: ToolStage;
  inspect?: ToolInspect;
  /** SimpleITK / scikit-image op run via Docker (`volume_tool.py`). */
  op?: string;
  needsReference?: boolean;
  fields?: ToolField[];
  comingSoon?: string;
  /** MenuBar nests this tool under `group › subgroup`. */
  subgroup?: string;
  /** See `ToolInteractive`. */
  interactive?: ToolInteractive;
  /** See `ToolItkKind`. */
  itkTool?: ToolItkKind;
  /** See `ToolDwiKind`. */
  dwiTool?: ToolDwiKind;
}

const always = () => true;

export const TOOL_TABS: { id: ToolTabId; label: string }[] = [
  { id: "preprocess", label: "Preprocess" },
  { id: "registration", label: "Registration" },
  { id: "segmentation", label: "Segmentation" },
  { id: "perfusion", label: "Perfusion" },
  { id: "quantification", label: "Quantification" },
];

export const TOOLS: ToolDef[] = [
  // ---------------------------------------------------------- Preprocess ---
  {
    id: "prep.brainMask",
    tab: "preprocess",
    group: "Brain Extraction",
    label: "Brain Mask",
    icon: "Brain",
    size: "large",
    description: "SynthStrip brain mask from the preprocessed DWI (OpenADS gen_mask).",
    engine: "OpenADS",
    status: "ready",
    stage: { modality: "DWI", id: "gen_mask" },
  },
  {
    id: "prep.skullStrip",
    tab: "preprocess",
    group: "Brain Extraction",
    label: "Skull Strip",
    icon: "Scissors",
    size: "large",
    description: "Apply the brain mask to DWI, ADC and b0 (OpenADS skull_strip).",
    engine: "OpenADS",
    status: "ready",
    stage: { modality: "DWI", id: "skull_strip" },
  },
  {
    id: "prep.applyMask",
    tab: "preprocess",
    group: "Brain Extraction",
    label: "Apply Brain Mask",
    icon: "Layers",
    size: "small",
    description: "Same as Skull Strip — OpenADS writes the masked volumes there.",
    engine: "OpenADS",
    status: "ready",
    stage: { modality: "DWI", id: "skull_strip" },
  },
  {
    id: "prep.prepdata",
    tab: "preprocess",
    group: "Data Preparation",
    label: "Prepare Data",
    icon: "FileInput",
    size: "small",
    description: "Harmonise raw DWI/ADC/b0 into the working copies the later stages read.",
    engine: "OpenADS",
    status: "ready",
    stage: { modality: "DWI", id: "prepdata" },
  },
  {
    id: "prep.imageOrientation",
    tab: "preprocess",
    group: "Orientation",
    label: "Image Orientation",
    icon: "Compass",
    size: "small",
    description: "Show qform/sform orientation of the selected volume.",
    engine: "NiBabel / OpenADS Desktop",
    status: "ready",
    inspect: "header",
  },
  {
    id: "prep.reorient",
    tab: "preprocess",
    group: "Orientation",
    label: "Reorient RAS+",
    icon: "RotateCw",
    size: "large",
    description: "Reorient a volume to RAS+.",
    engine: "SimpleITK",
    status: "ready",
    op: "reorient_ras",
  },
  {
    id: "prep.flip",
    tab: "preprocess",
    group: "Orientation",
    label: "Flip",
    icon: "FlipHorizontal2",
    size: "small",
    description: "Flip an axis.",
    engine: "SimpleITK",
    status: "ready",
    op: "flip",
    fields: [
      {
        id: "axis",
        label: "Axis",
        kind: "select",
        value: "0",
        options: [
          { value: "0", label: "X" },
          { value: "1", label: "Y" },
          { value: "2", label: "Z" },
        ],
      },
    ],
  },
  {
    id: "prep.permute",
    tab: "preprocess",
    group: "Orientation",
    label: "Permute Axes",
    icon: "Shuffle",
    size: "small",
    description: "Reorder voxel axes.",
    engine: "SimpleITK",
    status: "ready",
    op: "permute",
    fields: [{ id: "order", label: "Order", kind: "number", value: "2,1,0" }],
  },
  {
    id: "prep.resample",
    tab: "preprocess",
    group: "Geometry",
    subgroup: "Resample",
    label: "By Spacing",
    icon: "Scaling",
    size: "large",
    description: "Resample to a new voxel spacing.",
    engine: "SimpleITK",
    status: "ready",
    op: "resample_spacing",
    fields: [{ id: "spacing", label: "Spacing (mm)", kind: "number", value: "1,1,1" }],
  },
  {
    id: "prep.resampleSize",
    tab: "preprocess",
    group: "Geometry",
    subgroup: "Resample",
    label: "By Size",
    icon: "Scaling",
    size: "small",
    description: "Resample to a target matrix size.",
    engine: "SimpleITK",
    status: "ready",
    op: "resample_size",
    fields: [{ id: "size", label: "Matrix size", kind: "number", value: "128,128,128" }],
  },
  {
    id: "prep.resampleRef",
    tab: "preprocess",
    group: "Geometry",
    subgroup: "Resample",
    label: "To Reference",
    icon: "Scaling",
    size: "small",
    description: "Resample onto another volume's grid.",
    engine: "SimpleITK",
    status: "ready",
    op: "resample_reference",
    needsReference: true,
  },
  {
    id: "prep.crop",
    tab: "preprocess",
    group: "Geometry",
    label: "Crop",
    icon: "Crop",
    size: "small",
    description: "Crop to a bounding box.",
    engine: "SimpleITK",
    status: "ready",
    op: "crop",
    fields: [{ id: "margin", label: "Margin (vox)", kind: "number", value: "4" }],
  },
  {
    id: "prep.pad",
    tab: "preprocess",
    group: "Geometry",
    label: "Pad",
    icon: "Square",
    size: "small",
    description: "Pad to a target shape.",
    engine: "SimpleITK",
    status: "ready",
    op: "pad",
    fields: [{ id: "pad", label: "Pad (vox)", kind: "number", value: "4" }],
  },
  {
    id: "prep.extract",
    tab: "preprocess",
    group: "Geometry",
    label: "Extract Volume",
    icon: "Layers",
    size: "small",
    description: "Take one 3D volume out of a 4D series.",
    engine: "SimpleITK",
    status: "ready",
    op: "extract",
    fields: [{ id: "index", label: "Volume index", kind: "number", value: "0" }],
  },
  {
    id: "prep.normalize",
    tab: "preprocess",
    group: "Intensity",
    label: "Normalize",
    icon: "Gauge",
    size: "large",
    description: "Intensity normalisation.",
    engine: "SimpleITK",
    status: "ready",
    op: "normalize",
  },
  {
    id: "prep.histogramMatch",
    tab: "preprocess",
    group: "Intensity",
    label: "Histogram Match",
    icon: "ChartLine",
    size: "small",
    description: "Match intensities to a reference volume.",
    engine: "SimpleITK",
    status: "ready",
    op: "histogram_match",
    needsReference: true,
  },
  {
    id: "prep.rescale",
    tab: "preprocess",
    group: "Intensity",
    label: "Rescale Intensity",
    icon: "Gauge",
    size: "small",
    description: "Rescale intensities to a target range.",
    engine: "SimpleITK",
    status: "ready",
    op: "rescale",
    fields: [
      { id: "outMin", label: "Min", kind: "number", value: "0" },
      { id: "outMax", label: "Max", kind: "number", value: "1" },
    ],
  },
  {
    id: "prep.clamp",
    tab: "preprocess",
    group: "Intensity",
    label: "Clamp Intensity",
    icon: "Minimize2",
    size: "small",
    description: "Clamp voxel values to a range.",
    engine: "SimpleITK",
    status: "ready",
    op: "clamp",
    fields: [
      { id: "min", label: "Min", kind: "number", value: "0" },
      { id: "max", label: "Max", kind: "number", value: "100" },
    ],
  },
  {
    id: "prep.cast",
    tab: "preprocess",
    group: "Intensity",
    label: "Cast Data Type",
    icon: "FileType2",
    size: "small",
    description: "Cast the volume to uint8, int16 or float32.",
    engine: "SimpleITK",
    status: "ready",
    op: "cast",
    fields: [
      {
        id: "pixelType",
        label: "Type",
        kind: "select",
        value: "float32",
        options: [
          { value: "uint8", label: "uint8" },
          { value: "int16", label: "int16" },
          { value: "float32", label: "float32" },
        ],
      },
    ],
  },
  {
    id: "prep.n4",
    tab: "preprocess",
    group: "Correction",
    label: "N4 Bias Correction",
    icon: "Sun",
    size: "large",
    description: "N4 bias-field correction using SimpleITK.",
    engine: "SimpleITK N4",
    status: "ready",
    op: "n4",
  },
  {
    id: "prep.denoise",
    tab: "preprocess",
    group: "Correction",
    label: "Denoise",
    icon: "Sparkles",
    size: "small",
    description: "ANTs / SimpleITK denoise.",
    engine: "SimpleITK",
    status: "ready",
    op: "denoise",
    fields: [{ id: "iterations", label: "Iterations", kind: "number", value: "5" }],
  },
  {
    id: "prep.smooth",
    tab: "preprocess",
    group: "Correction",
    label: "Gaussian Smooth",
    icon: "Circle",
    size: "small",
    description: "Gaussian smoothing.",
    engine: "SimpleITK",
    status: "ready",
    op: "gaussian",
    fields: [{ id: "sigma", label: "Sigma (mm)", kind: "number", value: "1" }],
  },
  {
    id: "prep.median",
    tab: "preprocess",
    group: "Filtering",
    label: "Median Filter",
    icon: "Circle",
    size: "small",
    description: "Median filter.",
    engine: "SimpleITK / SciPy",
    status: "ready",
    op: "median",
    fields: [{ id: "radius", label: "Radius", kind: "number", value: "1" }],
  },
  {
    id: "prep.bilateral",
    tab: "preprocess",
    group: "Filtering",
    label: "Bilateral Filter",
    icon: "Sparkles",
    size: "small",
    description: "Edge-preserving bilateral filter.",
    engine: "SimpleITK",
    status: "ready",
    op: "bilateral",
  },
  {
    id: "prep.gradient",
    tab: "preprocess",
    group: "Filtering",
    label: "Gradient Magnitude",
    icon: "ChartLine",
    size: "small",
    description: "Gradient magnitude of the volume.",
    engine: "SimpleITK",
    status: "ready",
    op: "gradient_magnitude",
  },
  {
    id: "prep.header",
    tab: "preprocess",
    group: "Header & Metadata Tools",
    label: "NIfTI Header",
    icon: "FileDigit",
    size: "large",
    description: "Read dimensions, spacing, orientation and datatype from the selected volume.",
    engine: "OpenADS Desktop",
    status: "ready",
    inspect: "header",
  },
  {
    id: "prep.info",
    tab: "preprocess",
    group: "Header & Metadata Tools",
    label: "Image Info",
    icon: "Info",
    size: "small",
    description: "Same header readout, shown in the Properties panel.",
    engine: "OpenADS Desktop",
    status: "ready",
    inspect: "header",
  },

  {
    id: "prep.affineMatrix",
    tab: "preprocess",
    group: "Header & Metadata Tools",
    label: "Affine Matrix",
    icon: "Grid2x2",
    size: "small",
    description: "Show the image affine from the NIfTI header.",
    engine: "NiBabel / OpenADS Desktop",
    status: "ready",
    inspect: "header",
  },
  {
    id: "prep.qform",
    tab: "preprocess",
    group: "Header & Metadata Tools",
    label: "qform / sform",
    icon: "FileDigit",
    size: "small",
    description: "Show qform and sform codes from the header.",
    engine: "NiBabel / OpenADS Desktop",
    status: "ready",
    inspect: "header",
  },
  {
    id: "prep.copyAffine",
    tab: "preprocess",
    group: "Header & Metadata Tools",
    label: "Copy Affine",
    icon: "Copy",
    size: "small",
    description: "Copy origin, spacing and direction from a reference volume.",
    engine: "SimpleITK",
    status: "ready",
    op: "copy_information",
    needsReference: true,
  },
  {
    id: "prep.saveNifti",
    tab: "preprocess",
    group: "Header & Metadata Tools",
    label: "Save As NIfTI",
    icon: "Save",
    size: "small",
    description: "Rewrite the selected volume as compressed NIfTI in the case tools folder.",
    engine: "SimpleITK",
    status: "ready",
    op: "save_nifti",
  },
  {
    id: "prep.mathAdd",
    tab: "preprocess",
    group: "Image Math",
    label: "Add Images",
    icon: "Layers",
    size: "small",
    description: "Add two volumes.",
    engine: "NumPy / SimpleITK",
    status: "ready",
    op: "add",
    needsReference: true,
  },
  {
    id: "prep.mathSub",
    tab: "preprocess",
    group: "Image Math",
    label: "Subtract Images",
    icon: "GitCompare",
    size: "small",
    description: "Subtract a second volume from the input.",
    engine: "NumPy / SimpleITK",
    status: "ready",
    op: "subtract",
    needsReference: true,
  },
  {
    id: "prep.mathMul",
    tab: "preprocess",
    group: "Image Math",
    label: "Multiply Images",
    icon: "Grid2x2",
    size: "small",
    description: "Multiply two volumes.",
    engine: "NumPy / SimpleITK",
    status: "ready",
    op: "multiply",
    needsReference: true,
  },
  {
    id: "prep.mathAbs",
    tab: "preprocess",
    group: "Image Math",
    label: "Absolute Value",
    icon: "Layers",
    size: "small",
    description: "Absolute value of the volume.",
    engine: "NumPy / SimpleITK",
    status: "ready",
    op: "abs",
  },
  {
    id: "prep.mathMask",
    tab: "preprocess",
    group: "Image Math",
    label: "Mask Image",
    icon: "Layers",
    size: "small",
    description: "Apply a mask volume to the input.",
    engine: "NumPy / SimpleITK",
    status: "ready",
    op: "mask_image",
    needsReference: true,
  },
  {
    id: "reg.affinesyn",
    tab: "registration",
    group: "Registration Methods",
    label: "Affine+SyN",
    icon: "Spline",
    size: "large",
    description: "ANTs Affine + SyN to the JHU MNI152 template (OpenADS registration).",
    engine: "OpenADS / ANTsPyX",
    status: "ready",
    stage: { modality: "DWI", id: "registration" },
  },
  {
    id: "reg.quick",
    tab: "registration",
    group: "Registration Methods",
    label: "Quick Register",
    icon: "Zap",
    size: "small",
    description: "Same OpenADS registration stage — there is no separate rigid-only CLI yet.",
    engine: "OpenADS / ANTsPyX",
    status: "ready",
    stage: { modality: "DWI", id: "registration" },
  },
  {
    id: "reg.rigid",
    tab: "registration",
    group: "Registration Methods",
    label: "Rigid",
    icon: "Move",
    size: "small",
    description: "Rigid-only registration.",
    engine: "ANTsPyX",
    status: "future",
    comingSoon: "OpenADS always runs Affine+SyN; a rigid-only wrapper is not exposed.",
  },
  {
    id: "reg.affine",
    tab: "registration",
    group: "Registration Methods",
    label: "Affine",
    icon: "Move",
    size: "small",
    description: "Affine-only registration.",
    engine: "ANTsPyX",
    status: "future",
    comingSoon: "OpenADS always runs Affine+SyN; an affine-only wrapper is not exposed.",
  },
  {
    id: "reg.syn",
    tab: "registration",
    group: "Registration Methods",
    label: "SyN",
    icon: "Spline",
    size: "small",
    description: "SyN-only deformable registration.",
    engine: "ANTsPyX",
    status: "future",
    comingSoon: "A SyN-only wrapper is not exposed.",
  },
  {
    id: "reg.similarity",
    tab: "registration",
    group: "Registration Methods",
    label: "Similarity",
    icon: "Maximize2",
    size: "small",
    description: "Similarity (rigid + isotropic scale) registration.",
    engine: "ANTsPyX",
    status: "future",
    comingSoon: "A similarity-transform wrapper is not exposed.",
  },
  {
    id: "reg.dwiMni",
    tab: "registration",
    group: "Standard Registration Targets",
    label: "DWI → MNI",
    icon: "Brain",
    size: "large",
    description: "Register DWI/ADC to MNI152.",
    engine: "OpenADS",
    status: "ready",
    stage: { modality: "DWI", id: "registration" },
  },
  {
    id: "reg.adcMni",
    tab: "registration",
    group: "Standard Registration Targets",
    label: "ADC → MNI",
    icon: "Layers",
    size: "small",
    description: "ADC is carried with the DWI registration.",
    engine: "OpenADS",
    status: "ready",
    stage: { modality: "DWI", id: "registration" },
  },
  {
    id: "reg.ttpDwi",
    tab: "registration",
    group: "Standard Registration Targets",
    label: "TTP → DWI",
    icon: "GitCompare",
    size: "small",
    description: "Coregister TTP to ADC/DWI, then into MNI (OpenADS ttpadc_coreg).",
    engine: "OpenADS",
    status: "ready",
    stage: { modality: "PWI", id: "ttpadc_coreg" },
  },
  {
    id: "reg.apply",
    tab: "registration",
    group: "Transform",
    subgroup: "Apply",
    label: "Apply Transform",
    icon: "Forward",
    size: "large",
    description: "Apply an existing ANTs transform to another image.",
    engine: "ANTsPyX",
    status: "future",
    comingSoon: "Apply-transform is not a standalone OpenADS command yet.",
  },
  {
    id: "reg.applyLabel",
    tab: "registration",
    group: "Transform",
    subgroup: "Apply",
    label: "Apply to Label",
    icon: "Forward",
    size: "small",
    description: "Apply an existing transform to a label / mask with nearest-neighbour.",
    engine: "ANTsPyX",
    status: "future",
    comingSoon: "Apply-transform is not a standalone OpenADS command yet.",
  },
  {
    id: "reg.invert",
    tab: "registration",
    group: "Transform",
    subgroup: "Apply",
    label: "Invert Transform",
    icon: "Undo2",
    size: "small",
    description: "Invert an ANTs affine / warp.",
    engine: "ANTsPyX",
    status: "future",
    comingSoon: "Not wrapped yet.",
  },
  {
    id: "reg.compose",
    tab: "registration",
    group: "Transform",
    subgroup: "Apply",
    label: "Compose Transforms",
    icon: "GitMerge",
    size: "small",
    description: "Compose two ANTs transforms.",
    engine: "ANTsPyX",
    status: "future",
    comingSoon: "Not wrapped yet.",
  },
  {
    id: "reg.inspectTx",
    tab: "registration",
    group: "Transform",
    label: "Inspect Transform",
    icon: "Search",
    size: "small",
    description: "Open the case's transform files in the Study Explorer.",
    engine: "OpenADS Desktop",
    status: "ready",
    inspect: "viewer",
  },
  {
    id: "reg.overlay",
    tab: "registration",
    group: "Registration Quality Control",
    label: "Overlay",
    icon: "Layers2",
    size: "large",
    description: "Open the viewer with the current overlay settings.",
    engine: "OpenADS Desktop",
    status: "ready",
    inspect: "viewer-overlay",
  },
  {
    id: "reg.similarityQc",
    tab: "registration",
    group: "Registration Quality Control",
    label: "Image Similarity",
    icon: "Target",
    size: "small",
    description: "Correlation and mean-squared error between two volumes on the same grid.",
    engine: "NumPy / SimpleITK",
    status: "ready",
    op: "similarity",
    inspect: "stats",
    needsReference: true,
  },
  {
    id: "reg.difference",
    tab: "registration",
    group: "Registration Quality Control",
    label: "Difference Image",
    icon: "GitCompare",
    size: "small",
    description: "Moving minus fixed after registration.",
    engine: "SimpleITK",
    status: "ready",
    op: "subtract",
    needsReference: true,
  },
  {
    id: "reg.jacobian",
    tab: "registration",
    group: "Registration Quality Control",
    label: "Jacobian Map",
    icon: "Map",
    size: "small",
    description: "Jacobian determinant of the warp.",
    engine: "ANTsPyX",
    status: "future",
    comingSoon: "Not wrapped yet.",
  },
  {
    id: "reg.checkerboard",
    tab: "registration",
    group: "Registration Quality Control",
    label: "Checkerboard",
    icon: "Grid2x2",
    size: "small",
    description: "Checkerboard QC.",
    engine: "NiiVue",
    status: "future",
    comingSoon: "Checkerboard composite is not wired in the v2 viewer yet.",
  },

  // -------------------------------------------------------- Segmentation ---
  {
    id: "seg.stroke",
    tab: "segmentation",
    group: "AI Models",
    label: "Stroke Core",
    icon: "Target",
    size: "large",
    description: "DAGMNet_CH3 stroke-core segmentation (OpenADS DWI inference).",
    engine: "OpenADS",
    status: "ready",
    stage: { modality: "DWI", id: "inference" },
  },
  {
    id: "seg.hp",
    tab: "segmentation",
    group: "AI Models",
    label: "Hypoperfusion",
    icon: "Waves",
    size: "large",
    description: "3DUNet_PWI_Ch4 hypoperfusion mask (OpenADS PWI inference).",
    engine: "OpenADS",
    status: "ready",
    stage: { modality: "PWI", id: "inference" },
  },
  {
    id: "seg.brain",
    tab: "segmentation",
    group: "AI Models",
    label: "Brain Mask",
    icon: "Brain",
    size: "large",
    description: "SynthStrip brain mask.",
    engine: "OpenADS",
    status: "ready",
    stage: { modality: "DWI", id: "gen_mask" },
  },
  {
    id: "seg.threshold",
    tab: "segmentation",
    group: "Threshold-Based Segmentation",
    label: "Intensity Threshold",
    icon: "SlidersHorizontal",
    size: "large",
    description: "Threshold a volume into a mask.",
    engine: "SimpleITK",
    status: "ready",
    op: "threshold",
    fields: [{ id: "value", label: "Threshold", kind: "number", value: "0" }],
  },
  {
    id: "seg.otsu",
    tab: "segmentation",
    group: "Threshold-Based Segmentation",
    label: "Otsu",
    icon: "Contrast",
    size: "small",
    description: "Otsu threshold.",
    engine: "SimpleITK",
    status: "ready",
    op: "otsu",
  },
  {
    id: "seg.multiOtsu",
    tab: "segmentation",
    group: "Threshold-Based Segmentation",
    label: "Multi-Otsu",
    icon: "Contrast",
    size: "small",
    description: "Multi-class Otsu thresholds.",
    engine: "SimpleITK",
    status: "ready",
    op: "multi_otsu",
    fields: [{ id: "classes", label: "Classes", kind: "number", value: "2" }],
  },
  {
    id: "seg.components",
    tab: "segmentation",
    group: "Components",
    label: "Largest Component",
    icon: "Boxes",
    size: "large",
    description: "Keep the largest connected component.",
    engine: "SimpleITK / scikit-image",
    status: "ready",
    op: "largest_component",
  },
  {
    id: "seg.connectedComponents",
    tab: "segmentation",
    group: "Components",
    label: "Connected Components",
    icon: "Boxes",
    size: "small",
    description: "Label connected components in a mask.",
    engine: "SimpleITK / scikit-image",
    status: "ready",
    op: "connected_components",
  },
  {
    id: "seg.removeSmall",
    tab: "segmentation",
    group: "Components",
    label: "Remove Small Objects",
    icon: "Eraser",
    size: "small",
    description: "Drop components below a voxel threshold.",
    engine: "SimpleITK / scikit-image",
    status: "ready",
    op: "remove_small",
    fields: [{ id: "minSize", label: "Min voxels", kind: "number", value: "64" }],
  },
  {
    id: "seg.relabel",
    tab: "segmentation",
    group: "Components",
    label: "Relabel Components",
    icon: "Boxes",
    size: "small",
    description: "Relabel connected components by size.",
    engine: "SimpleITK",
    status: "ready",
    op: "relabel",
  },
  {
    id: "seg.dilate",
    tab: "segmentation",
    group: "Morphology",
    label: "Dilate",
    icon: "Maximize2",
    size: "small",
    description: "Binary dilate a mask.",
    engine: "SimpleITK / scikit-image",
    status: "ready",
    op: "dilate",
    fields: [{ id: "radius", label: "Radius", kind: "number", value: "1" }],
  },
  {
    id: "seg.erode",
    tab: "segmentation",
    group: "Morphology",
    label: "Erode",
    icon: "Minimize2",
    size: "small",
    description: "Binary erode a mask.",
    engine: "SimpleITK / scikit-image",
    status: "ready",
    op: "erode",
    fields: [{ id: "radius", label: "Radius", kind: "number", value: "1" }],
  },
  {
    id: "seg.open",
    tab: "segmentation",
    group: "Morphology",
    label: "Open",
    icon: "Circle",
    size: "small",
    description: "Morphological opening.",
    engine: "SimpleITK / scikit-image",
    status: "ready",
    op: "open",
    fields: [{ id: "radius", label: "Radius", kind: "number", value: "1" }],
  },
  {
    id: "seg.close",
    tab: "segmentation",
    group: "Morphology",
    label: "Close",
    icon: "Circle",
    size: "small",
    description: "Morphological closing.",
    engine: "SimpleITK / scikit-image",
    status: "ready",
    op: "close",
    fields: [{ id: "radius", label: "Radius", kind: "number", value: "1" }],
  },
  {
    id: "seg.fillHoles",
    tab: "segmentation",
    group: "Morphology",
    label: "Fill Holes",
    icon: "Droplet",
    size: "small",
    description: "Fill holes in a binary mask.",
    engine: "SimpleITK / SciPy",
    status: "ready",
    op: "fill_holes",
  },
  {
    id: "seg.maskAnd",
    tab: "segmentation",
    group: "Mask Combination",
    label: "Mask AND",
    icon: "Layers",
    size: "small",
    description: "Intersection of two masks.",
    engine: "NumPy / SimpleITK",
    status: "ready",
    op: "mask_and",
    needsReference: true,
  },
  {
    id: "seg.maskOr",
    tab: "segmentation",
    group: "Mask Combination",
    label: "Mask OR",
    icon: "Layers",
    size: "small",
    description: "Union of two masks.",
    engine: "NumPy / SimpleITK",
    status: "ready",
    op: "mask_or",
    needsReference: true,
  },
  {
    id: "seg.maskXor",
    tab: "segmentation",
    group: "Mask Combination",
    label: "Mask XOR",
    icon: "GitCompare",
    size: "small",
    description: "Symmetric difference of two masks.",
    engine: "NumPy / SimpleITK",
    status: "ready",
    op: "mask_xor",
    needsReference: true,
  },
  {
    id: "seg.maskNot",
    tab: "segmentation",
    group: "Mask Combination",
    label: "Mask NOT",
    icon: "Eraser",
    size: "small",
    description: "Invert a binary mask.",
    engine: "NumPy / SimpleITK",
    status: "ready",
    op: "mask_not",
  },
  {
    id: "seg.bbox",
    tab: "segmentation",
    group: "Shape & Size Measurements",
    label: "Bounding Box",
    icon: "Crop",
    size: "small",
    description: "Bounding box of the largest component.",
    engine: "SimpleITK",
    status: "ready",
    op: "bbox",
    inspect: "stats",
  },
  {
    id: "seg.centroid",
    tab: "segmentation",
    group: "Shape & Size Measurements",
    label: "Centroid",
    icon: "Target",
    size: "small",
    description: "Centroid of the largest component.",
    engine: "SimpleITK",
    status: "ready",
    op: "centroid",
    inspect: "stats",
  },
  {
    id: "seg.componentCount",
    tab: "segmentation",
    group: "Shape & Size Measurements",
    label: "Component Count",
    icon: "Boxes",
    size: "small",
    description: "Count connected components in a mask.",
    engine: "SimpleITK",
    status: "ready",
    op: "component_count",
    inspect: "stats",
  },
  {
    id: "seg.volume",
    tab: "segmentation",
    group: "Shape & Size Measurements",
    label: "Volume",
    icon: "Ruler",
    size: "large",
    description: "Measure the loaded overlay on Study, and show protocol volumes when reporting files exist.",
    engine: "OpenADS",
    status: "ready",
    inspect: "results",
  },

  // ----------------------------------------------------------- Perfusion ---
  {
    id: "pwi.prepare",
    tab: "perfusion",
    group: "Prepare",
    label: "Prepare PWI",
    icon: "FileInput",
    size: "large",
    description: "Slice-timing, motion correction, Gd series and PWI brain mask.",
    engine: "OpenADS",
    status: "ready",
    stage: { modality: "PWI", id: "prepdata" },
  },
  {
    id: "pwi.validate",
    tab: "perfusion",
    group: "Prepare",
    label: "Validate 4D Series",
    icon: "ShieldCheck",
    size: "small",
    description: "Re-scan the subject folder and check PWI readiness.",
    engine: "OpenADS Desktop",
    status: "ready",
    inspect: "header",
  },
  {
    id: "pwi.ttp",
    tab: "perfusion",
    group: "Maps",
    label: "Generate TTP",
    icon: "Timer",
    size: "large",
    description: "Time-to-peak map from the Gd concentration series.",
    engine: "OpenADS",
    status: "ready",
    stage: { modality: "PWI", id: "gen_ttp" },
  },
  {
    id: "pwi.tmax",
    tab: "perfusion",
    group: "Maps",
    label: "Tmax",
    icon: "Clock",
    size: "small",
    description: "Tmax map.",
    engine: "future",
    status: "future",
    comingSoon: "OpenADS currently writes TTP, not Tmax/CBF/CBV/MTT.",
  },
  {
    id: "pwi.coreg",
    tab: "perfusion",
    group: "Registration",
    label: "TTP → DWI",
    icon: "GitCompare",
    size: "large",
    description: "Coregister TTP to ADC and carry it into MNI.",
    engine: "OpenADS",
    status: "ready",
    stage: { modality: "PWI", id: "ttpadc_coreg" },
  },
  {
    id: "pwi.hp",
    tab: "perfusion",
    group: "Segmentation",
    label: "Hypoperfusion",
    icon: "Waves",
    size: "large",
    description: "PWI hypoperfusion segmentation.",
    engine: "OpenADS",
    status: "ready",
    stage: { modality: "PWI", id: "inference" },
  },
  {
    id: "pwi.mismatch",
    tab: "perfusion",
    group: "Mismatch",
    label: "Mismatch",
    icon: "GitCompare",
    size: "large",
    description: "Core, HP and mismatch: live overlay plus protocol numbers when both reports exist.",
    engine: "OpenADS",
    status: "ready",
    inspect: "results",
  },

  // ------------------------------------------------------ Quantification ---
  {
    id: "q.coreVolume",
    tab: "quantification",
    group: "Lesion Volume",
    label: "Lesion Volume",
    icon: "Ruler",
    size: "large",
    description: "Live overlay volume plus the reporting-file core volume when it exists.",
    engine: "OpenADS",
    status: "ready",
    inspect: "results",
  },
  {
    id: "q.hpVolume",
    tab: "quantification",
    group: "Lesion Volume",
    label: "HP Volume",
    icon: "Waves",
    size: "small",
    description: "Protocol HP volume from the PWI report — blank until that file exists.",
    engine: "OpenADS",
    status: "ready",
    inspect: "results",
  },
  {
    id: "q.aspects",
    tab: "quantification",
    group: "Stroke Scores",
    label: "ASPECTS",
    icon: "Table2",
    size: "large",
    description: "ASPECTS from atlas tables. Needs an MNI lesion mask and Reporting.",
    engine: "OpenADS",
    status: "ready",
    inspect: "results",
  },
  {
    id: "q.qfv",
    tab: "quantification",
    group: "QFV",
    label: "QFV Table",
    icon: "Sheet",
    size: "large",
    description: "Atlas QFV tables from Reporting. Not estimated from the overlay alone.",
    engine: "OpenADS",
    status: "ready",
    inspect: "results",
  },
  {
    id: "q.regional",
    tab: "quantification",
    group: "Regional",
    label: "Regional Load",
    icon: "Map",
    size: "large",
    description: "Lesion-load tables per atlas, written by Reporting.",
    engine: "OpenADS",
    status: "ready",
    inspect: "results",
  },
  {
    id: "q.report",
    tab: "quantification",
    group: "Reports",
    label: "Generate Report",
    icon: "FilePlus2",
    size: "large",
    description: "Run the OpenADS reporting stage (atlas tables, figures, radiological text).",
    engine: "OpenADS",
    status: "ready",
    stage: { modality: "DWI", id: "report" },
  },
  {
    id: "q.pwiReport",
    tab: "quantification",
    group: "Reports",
    label: "PWI Report",
    icon: "FileText",
    size: "small",
    description: "PWI reporting stage.",
    engine: "OpenADS",
    status: "ready",
    stage: { modality: "PWI", id: "report" },
  },

  // --------------------------------------------------------------- ML/DL ---
  // Plug-and-play third-party models, not OpenADS's own. No `tab`: these
  // live only in the hand-authored `tools.learning` group in `nav.config.ts`,
  // not in the ribbon or a generated per-tab bucket (see `ToolDef.tab`).
  {
    id: "ml.sam",
    group: "ML / DL",
    label: "Segment Anything (SAM)",
    icon: "MousePointerClick",
    size: "large",
    description:
      "Click a point on the current slice to segment the region under it — Meta's Segment Anything (ViT-B), running entirely in your browser via ONNX Runtime Web. Nothing is uploaded.",
    engine: "ONNX Runtime Web — SAM ViT-B (int8)",
    status: "ready",
    interactive: "sam-points",
  },
  {
    id: "ml.medsam",
    group: "ML / DL",
    label: "MedSAM",
    icon: "BoxSelect",
    size: "large",
    description: "Box-prompt segmentation tuned for medical images.",
    engine: "ONNX Runtime Web",
    status: "future",
    comingSoon:
      "No official, redistributable ONNX export of MedSAM or LiteMedSAM could be found yet (checked bowang-lab/MedSAM and Hugging Face, 2026-10-01). Wiring this would mean converting the checkpoint ourselves, which this tool deliberately avoids — revisit if one is published.",
  },
  // --------------------------------------------- Image Processing (itk-wasm) ---
  // Real, officially-published, pre-built WASM pipelines from the ITK-Wasm
  // project (InsightSoftwareConsortium/ITK-Wasm and
  // KitwareMedical/ITKMorphologicalContourInterpolation) — we compile none of
  // these ourselves. Each package's own `.wasm` is fetched lazily (jsDelivr
  // CDN) only when the tool actually runs, never bundled or pre-downloaded —
  // same rule as `ml.sam`. See `tools/itk/*Client.ts` for the real calls, and
  // `scripts/verify-itk-wasm-tools.mjs` for the Node-side proof these run
  // correctly against real data before any UI was built around them.
  {
    id: "itk.downsample",
    group: "Image Processing",
    label: "Downsample Image",
    icon: "Minimize2",
    size: "large",
    description:
      "Smoothing anti-alias filter + subsample, by a factor per axis. Official @itk-wasm/downsample pipeline, runs entirely in your browser.",
    engine: "itk-wasm — @itk-wasm/downsample",
    status: "ready",
    itkTool: "downsample",
  },
  {
    id: "itk.compareImages",
    group: "Image Processing",
    label: "Compare Images (QC)",
    icon: "GitCompare",
    size: "large",
    description:
      "Voxelwise difference between two volumes with a tolerance, for registration/output QC. Official @itk-wasm/compare-images pipeline.",
    engine: "itk-wasm — @itk-wasm/compare-images",
    status: "ready",
    itkTool: "compareImages",
  },
  {
    id: "itk.morphInterp",
    group: "Image Processing",
    label: "Interpolate Segmentation Slices",
    icon: "Layers",
    size: "large",
    description:
      "Fills in missing slices of a segmentation mask between labeled slices (morphology-based interslice interpolation). Official @itk-wasm/morphological-contour-interpolation pipeline.",
    engine: "itk-wasm — @itk-wasm/morphological-contour-interpolation",
    status: "ready",
    itkTool: "morphInterp",
  },
  {
    id: "itk.dicomImport",
    group: "Image Processing",
    label: "Import DICOM Series",
    icon: "FolderInput",
    size: "large",
    description:
      "Reads a DICOM file series and converts it to a volume you can view and save as NIfTI. Official @itk-wasm/dicom pipeline; does not need a case already open.",
    engine: "itk-wasm — @itk-wasm/dicom",
    status: "ready",
    itkTool: "dicomImport",
  },
  // ------------------------------------------ DWI Pipeline Stages (standalone) ---
  // The same real OpenADS DWI computations the main pipeline runs
  // (`lib/dwi-pipeline.ts`), offered as standalone tools — pick the input
  // volume(s) yourself instead of running the whole 6-stage protocol. Real
  // ANTs (Affine+SyN) and real DAGMNet ONNX, same as a full pipeline run.
  {
    id: "dwi.register",
    group: "DWI Pipeline Stages",
    label: "Register to MNI Space",
    icon: "Crosshair",
    size: "large",
    description:
      "Real ANTs Affine + SyN registration to JHU MNI space, from a raw DWI + ADC pair. Runs SynthStrip first to get the brain mask it registers. Several minutes, single-threaded.",
    engine: "Emscripten — ANTs antsRegistration/antsApplyTransforms (real ANTsX/ANTs C++)",
    status: "ready",
    dwiTool: "register",
  },
  {
    id: "dwi.segmentStroke",
    group: "DWI Pipeline Stages",
    label: "Segment Stroke Core",
    icon: "Brain",
    size: "large",
    description:
      "Real DAGMNet DWI (ch3) ONNX lesion segmentation, from an MNI-registered DWI + ADC pair (use Register to MNI Space first, or a full pipeline run's output).",
    engine: "ONNX Runtime Web — DAGMNet DWI",
    status: "ready",
    dwiTool: "segmentStroke",
  },
];

export function toolsForTab(tab: ToolTabId): ToolDef[] {
  return TOOLS.filter((t) => t.tab === tab);
}

export function toolById(id: string): ToolDef | undefined {
  return TOOLS.find((t) => t.id === id);
}

/**
 * The one OpenADS stage with a real in-browser implementation today: see
 * `wasm-engine/engine.ts`'s own "Scope of this pass" note. `gen_mask` and
 * `skull_strip` are the same SynthStrip computation (`gen_mask` is folded
 * into `skull_strip`), so either stage id on a tool means this. Shared by
 * `nav/nav.config.ts` (which tools the Tools menu surfaces),
 * `tools/stageRun/StageRunPanel.tsx` (which tools it actually runs instead of
 * showing "not available yet"), and the nav-config tests, so the three
 * cannot drift apart.
 */
export function isBrainExtraction(tool: ToolDef): boolean {
  return tool.stage?.id === "skull_strip" || tool.stage?.id === "gen_mask";
}

export function toolGroups(tab: ToolTabId): { name: string; tools: ToolDef[] }[] {
  const groups: { name: string; tools: ToolDef[] }[] = [];
  for (const tool of toolsForTab(tab)) {
    const existing = groups.find((g) => g.name === tool.group);
    if (existing) existing.tools.push(tool);
    else groups.push({ name: tool.group, tools: [tool] });
  }
  return groups;
}

function toolItem(tool: ToolDef): RibbonItem {
  const future = tool.status === "future";
  return {
    id: tool.id,
    label: tool.label,
    icon: tool.icon,
    size: tool.size,
    action: `tool.select.${tool.id}`,
    tooltip: future ? tool.comingSoon : tool.description,
    disabled: future ? always : undefined,
    submenu: tool.subgroup,
  };
}

/** Ribbon tabs for the five v2 workspaces. */
export function toolRibbonTabs(): RibbonTab[] {
  return TOOL_TABS.map(({ id, label }) => ({
    id,
    label,
    groups: [
      ...toolGroups(id).map((group) => ({
        id: `${id}.${group.name.toLowerCase().replace(/\s+/g, "")}`,
        label: group.name,
        items: group.tools.map(toolItem),
      })),

    ],
  }));
}
